import { useStandardToast } from "@/app/hooks/useStandardToast";
import { Sponsorship } from "@/app/sponsorships/types";
import {
  getScaledDimensions,
  roundImageCorners,
  toDataURL,
} from "@/app/utils/image";
import { Button } from "@chakra-ui/react";
import { default as PPT, default as PptxGenJS } from "pptxgenjs";
import { useState } from "react";

const fontFace = "Aptos";
const labelProps = {
  fontSize: 12,
  bold: true,
  color: "#5cba00",
  breakLine: true,
  fontFace,
};
const valueProps = {
  fontSize: 14,
  fontFace,
};
const bgGrey = "#f2f2f2";

const rightColumnX = 0.69;
const leftColumnX = 4.01;
const cellOptions: PptxGenJS.TextPropsOptions = {
  align: "left",
  valign: "top",
};

export const ExportButton = ({
  sponsorships,
}: {
  sponsorships: Sponsorship[];
}) => {
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const toast = useStandardToast();

  const onClick = async () => {
    setIsExporting(true);
    try {
      const pres = await buildPresentation(sponsorships);
      await pres.writeFile({ fileName: "export.pptx" });
    } catch (error) {
      console.error(error);
      toast({
        title: `${error}`,
        status: "error",
      });
    }
    setIsExporting(false);
  };

  return (
    <Button
      data-cy="export-button"
      isDisabled={!sponsorships.length}
      onClick={onClick}
      variant="secondary"
      isLoading={isExporting}
    >
      Download PPT
    </Button>
  );
};

const getNotes = ({ comments, any_renewal_details }: Sponsorship) => {
  if (!comments && !any_renewal_details) {
    return "[Please enter any notes or relevant commentary on this sponsorship. Otherwise, delete this text box]";
  }
  return [comments, any_renewal_details].join("\n");
};

const getLocation = ({ country, city, region }: Sponsorship): string => {
  if (!!city.length) {
    return city.map(({ label }) => label).join(", ");
  }
  if (!!country.length) {
    return country.map(({ label }) => label).join(", ");
  }
  return region.label;
};

const buildPresentation = async (sponsorships: Sponsorship[]) => {
  let pres = new PPT();
  pres.layout = "LAYOUT_WIDE";
  for (let i = 0; i < sponsorships.length; i++) {
    const sponsorship = sponsorships[i];
    let slide = pres.addSlide();

    // header
    slide.addText(`${sponsorship.sponsor?.label} & ${sponsorship.property}`, {
      x: 0.58,
      y: 0.49,
      w: 11.08,
      h: 0.79,
      fontSize: 28,
      align: "left",
      valign: "top",
      fontFace,
      bold: true,
    });

    // table
    slide.addShape("roundRect", {
      w: 6.75,
      h: 3.43,
      x: 0.57,
      y: 1.29,
      rectRadius: 0.1,
      fill: {
        color: bgGrey,
      },
    });
    [
      {
        label: "Sponsor",
        value: `${sponsorship.sponsor?.label}`,
        x: rightColumnX,
        y: 1.42,
        w: 3.07,
        h: 1.0,
      },
      {
        label: "Estimated Annual Fee",
        value: !!sponsorship.total_price_annual
          ? `£${sponsorship.total_price_annual.toLocaleString()}`
          : "",
        x: rightColumnX,
        y: 2.42,
        w: 3.07,
        h: 0.8,
      },
      {
        label: "Year Deal Began",
        value: `${sponsorship.year_deal_agreed?.label || "-"}`,
        x: rightColumnX,
        y: 3.22,
        w: 3.07,
        h: 0.8,
      },
      {
        label: "Location",
        value: getLocation(sponsorship),
        x: rightColumnX,
        y: 4.02,
        w: 3.07,
        h: 0.8,
      },
      {
        label: "Property",
        value: `${sponsorship.property}`,
        x: leftColumnX,
        y: 1.42,
        w: 3.07,
        h: 1.0,
      },
      {
        label: "Estimated Total Fee",
        value: !!sponsorship.total_price_paid
          ? `£${sponsorship.total_price_paid.toLocaleString()}`
          : "-",
        x: leftColumnX,
        y: 2.42,
        w: 3.07,
        h: 0.8,
      },
      {
        label: "Duration",
        value: !!sponsorship.duration_of_deal
          ? `${sponsorship.duration_of_deal} years`
          : "-",
        x: leftColumnX,
        y: 3.22,
        w: 3.07,
        h: 0.8,
      },
      {
        label: "Annual Audience",
        value: !!sponsorship.footfall_audience_annual
          ? `${sponsorship.footfall_audience_annual.toLocaleString()}`
          : "-",
        x: leftColumnX,
        y: 4.02,
        w: 3.07,
        h: 0.8,
      },
    ].forEach(({ label, value, ...rest }) => {
      slide.addText(
        [
          { text: label, options: labelProps },
          { text: value, options: valueProps },
        ],
        { ...cellOptions, ...rest }
      );
    });

    // comments section
    slide.addShape("roundRect", {
      w: 6.75,
      h: 2.14,
      x: 0.57,
      y: 4.86,
      rectRadius: 0.1,
      fill: {
        color: bgGrey,
      },
    });
    slide.addText(
      [
        { text: "Notes", options: labelProps },
        { text: getNotes(sponsorship), options: valueProps },
      ],
      { w: 6.5, h: 1.91, x: 0.69, y: 4.97, align: "left", valign: "top" }
    );

    // main image
    if (!!sponsorship.image) {
      const dataUrl = await toDataURL(sponsorship.image);
      const { w, h } = await getScaledDimensions(dataUrl, 5.41, 4.54);
      let rounded = await roundImageCorners(dataUrl, w * 96, h * 96, 10);
      slide.addImage({
        path: `${rounded}`,
        x: 7.51,
        y: 1.29,
        w,
        h,
      });
    } else {
      slide.addShape("roundRect", {
        w: 5.41,
        h: 4.54,
        x: 7.51,
        y: 1.29,
        rectRadius: 0.1,
        fill: {
          color: bgGrey,
        },
      });
      slide.addText("[Please insert an image]", {
        w: 5.41,
        h: 4.54,
        x: 7.51,
        y: 1.29,
        fontSize: 12,
        align: "center",
        valign: "middle",
        fontFace,
      });
    }

    // page number
    slide.addImage({
      path: "/pg_number.png",
      w: 0.62,
      h: 0.46,
      x: 12.71,
      y: 7.03,
    });

    // sliver at top of slide
    slide.addShape("rect", {
      w: 1.75,
      h: 0.05,
      x: 1.13,
      y: 0,
      fill: {
        color: "#cbfb45",
      },
    });

    // top right icon
    slide.addImage({
      path: "/icon.png",
      w: 0.71,
      h: 0.66,
      x: 12.25,
      y: 0.32,
    });
  }
  return pres;
};
